import { Type, type Static } from '@sinclair/typebox';
import { FileUploadSchema } from '../../shared/file/file-request.schema';
import { PaginationQueryParamsSchema } from '../../shared/utils/pagination-query-params.schema';
import { StringEnum } from '../../utils/typebox';
import { PostIdAdditionalDescription } from '../post/post-request.schema';

export const GetPostCommentsListQuerySchema = Type.Intersect([
  PaginationQueryParamsSchema,
  Type.Object({
    sort_by: Type.Optional(
      StringEnum(['MOST_RECENT', 'MOST_RELEVANT'], {
        description: 'Sort criterion for the posts list: MOST_RECENT (most recent first) or MOST_RELEVANT (most relevant first).',
        default: 'MOST_RECENT',
      }),
    ),
  }),
]);

export const GetUserCommentsListQuerySchema = PaginationQueryParamsSchema;
export const GetUserReactionsListQuerySchema = PaginationQueryParamsSchema;

export const GetPostCommentsListParamsSchema = Type.Object({
  post_id: Type.String({
    minLength: 1,
    description: 'The ID of the Post to retrieve comments from.' + PostIdAdditionalDescription,
  }),
});

export const AddPostCommentParamsSchema = Type.Object({
  post_id: Type.String({ minLength: 1, description: 'The ID of the Post to add the comment to.' + PostIdAdditionalDescription }),
});

export const AddPostCommentBodySchema = Type.Object(
  {
    text: Type.String({
      description:
        'Textual content of the comment. User mentions can be added by inserting an @ followed by the `id` or `public_identifier` of the user (example: @JohnDoe).',
    }),
    comment_as: Type.Optional(
      Type.String({
        minLength: 1,
        description: 'The ID of the User on whose behalf the comment should be published (if supported by the provider).',
      }),
    ),
    attachments: Type.Optional(Type.Array(FileUploadSchema, { description: 'Attachment associated with the comment' })),
  },
  { additionalProperties: false },
);

export const UpdatePostCommentBodySchema = Type.Object({
  text: Type.String({
    description:
      'Updated textual content of the comment. User mentions can be added by inserting an @ followed by the `id` or `public_identifier` of the user (example: @JohnDoe).',
  }),
});

export const ReplyToCommentParamsSchema = Type.Object({
  post_id: Type.String({ minLength: 1, description: 'The ID of the Post to add the comment to.' + PostIdAdditionalDescription }),
  comment_id: Type.String({ minLength: 1, description: 'The ID of the Comment to reply to.' }),
});

export const ReplyToCommentBodySchema = AddPostCommentBodySchema;

export const UpdatePostCommentParamsSchema = Type.Object({
  post_id: Type.String({ minLength: 1, description: 'The ID of the Post where the comment is.' + PostIdAdditionalDescription }),
  comment_id: Type.String({ minLength: 1, description: 'The ID of the Comment to update.' }),
});

export const DeletePostCommentParamsSchema = Type.Object({
  post_id: Type.String({ minLength: 1, description: 'The ID of the Post where the comment is.' + PostIdAdditionalDescription }),
  comment_id: Type.String({ minLength: 1, description: 'The ID of the Comment to delete.' }),
});

export const GetPostCommentReactionsListQuerySchema = PaginationQueryParamsSchema;

export const GetPostCommentReactionsListParamsSchema = Type.Object({
  post_id: Type.String({ minLength: 1, description: 'The ID of the Post where the comment is.' + PostIdAdditionalDescription }),
  comment_id: Type.String({ minLength: 1, description: 'The ID of the Comment to retrieve reactions from.' }),
});

export const GetPostCommentRepliesListQuerySchema = PaginationQueryParamsSchema;

export const GetPostCommentRepliesListParamsSchema = Type.Object({
  post_id: Type.String({ minLength: 1, description: 'The ID of the Post where the comment is.' + PostIdAdditionalDescription }),
  comment_id: Type.String({ minLength: 1, description: 'The ID of the Comment to retrieve replies from.' }),
});

export const AddPostCommentReactionParamsSchema = Type.Object({
  post_id: Type.String({ minLength: 1, description: 'The ID of the Post where the comment is.' + PostIdAdditionalDescription }),
  comment_id: Type.String({ minLength: 1, description: 'The ID of the Comment to add the reaction to.' }),
});

export const RemovePostCommentReactionParamsSchema = Type.Object({
  post_id: Type.String({ minLength: 1, description: 'The ID of the Post where the comment is.' + PostIdAdditionalDescription }),
  comment_id: Type.String({ minLength: 1, description: 'The ID of the Comment where the reaction is.' }),
});

/**
 * @note Base schema, full schema in provider-shared
 */
export const AddPostCommentReactionBaseBodySchema = Type.Object({
  reaction: Type.String({
    description:
      'The unicode emoji if supported by the provider, otherwise the reaction type (prefixed by the provider of the targeted account).',
  }),
  react_as: Type.Optional(
    Type.String({
      minLength: 1,
      description: 'The ID of the User on whose behalf the reaction should be published (if supported by the provider).',
    }),
  ),
});

/**
 * @note Base schema, full schema in provider-shared
 */
export const RemovePostCommentReactionBaseBodySchema = Type.Object({
  reaction: Type.String({
    description:
      'The reaction to remove if needed by the provider. Might be the unicode emoji if supported, otherwise the reaction type (prefixed by the provider of the targeted account).',
  }),
  react_as: Type.Optional(
    Type.String({
      minLength: 1,
      description:
        'The ID of the User on whose behalf the reaction was published and should be removed (if supported by the provider).',
    }),
  ),
});

export const GetUserCommentsParamsSchema = Type.Object({
  user_id: Type.String({ minLength: 1, description: 'The ID of the User to retrieve the comments from.' }),
});
export const GetUserReactionsParamsSchema = Type.Object({
  user_id: Type.String({ minLength: 1, description: 'The ID of the User to retrieve the reactions from.' }),
});

export type GetPostCommentsListQuery = Static<typeof GetPostCommentsListQuerySchema>;
export type GetUserCommentsListQuery = Static<typeof GetUserCommentsListQuerySchema>;
export type GetUserReactionsQuery = Static<typeof GetUserReactionsListQuerySchema>;
export type GetPostCommentsListParams = Static<typeof GetPostCommentsListParamsSchema>;
export type AddPostCommentParams = Static<typeof AddPostCommentParamsSchema>;
export type AddPostCommentBody = Static<typeof AddPostCommentBodySchema>;
export type ReplyToCommentParams = Static<typeof ReplyToCommentParamsSchema>;
export type ReplyToCommentBody = Static<typeof ReplyToCommentBodySchema>;
export type UpdatePostCommentParams = Static<typeof UpdatePostCommentParamsSchema>;
export type UpdatePostCommentBody = Static<typeof UpdatePostCommentBodySchema>;
export type DeletePostCommentParams = Static<typeof DeletePostCommentParamsSchema>;
export type GetPostCommentReactionsListQuery = Static<typeof GetPostCommentReactionsListQuerySchema>;
export type GetPostCommentReactionsListParams = Static<typeof GetPostCommentReactionsListParamsSchema>;
export type GetPostCommentRepliesListQuery = Static<typeof GetPostCommentRepliesListQuerySchema>;
export type GetPostCommentRepliesListParams = Static<typeof GetPostCommentRepliesListParamsSchema>;
export type AddPostCommentReactionParams = Static<typeof AddPostCommentReactionParamsSchema>;
export type AddPostCommentReactionBaseBody = Static<typeof AddPostCommentReactionBaseBodySchema>;
export type RemovePostCommentReactionParams = Static<typeof RemovePostCommentReactionParamsSchema>;
export type RemovePostCommentReactionBaseBody = Static<typeof RemovePostCommentReactionBaseBodySchema>;
export type GetUserCommentsParams = Static<typeof GetUserCommentsParamsSchema>;
export type GetUserReactionsParams = Static<typeof GetUserReactionsParamsSchema>;
